import { useState } from "react";
import { ArrowUpRight, Minus, Plus, Users } from "lucide-react";
import { PLANS, EVENT, GROUP_DISCOUNT_TIERS, GROUP_DISCOUNT_MIN } from "../data/eventData";
import { MaskLine, Reveal } from "./Motion";

const brl = (v) => v.toLocaleString("pt-BR", { style: "currency", currency: "BRL" });

const discountFor = (qty) =>
  (GROUP_DISCOUNT_TIERS.find((t) => qty >= t.min && qty <= t.max) || { pct: 0 }).pct;

const PlanCard = ({ plan, onCTA }) => {
  const [qty, setQty] = useState(1);
  const pct = discountFor(qty);
  const hasDiscount = pct > 0;
  const unit = plan.priceValue * (1 - pct / 100);
  const total = unit * qty;

  return (
    <div
      className={`relative h-full rounded-3xl overflow-hidden flex flex-col transition-all duration-500 ${
        plan.highlight ? "glass-static ring-glow" : "glass-static"
      }`}
      data-testid={`plan-card-${plan.id}`}
    >
      {/* Tarja superior colorida */}
      {plan.id === "presencial" ? (
        <div className="bg-gradient-to-r from-brand-blue to-brand-cyan px-7 sm:px-9 py-4" data-testid="plan-strip-presencial">
          <h3 className="font-display font-extrabold uppercase text-xl sm:text-2xl text-brand-deep tracking-tight">
            Presencial
          </h3>
        </div>
      ) : (
        <div className="bg-gradient-to-r from-red-700 to-red-500 px-7 sm:px-9 py-4 flex items-center gap-2.5" data-testid="plan-strip-online">
          <span className="relative flex h-2.5 w-2.5">
            <span className="absolute inline-flex h-full w-full rounded-full bg-white opacity-75 animate-ping" />
            <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-white" />
          </span>
          <h3 className="font-display font-extrabold uppercase text-xl sm:text-2xl text-white tracking-tight">
            Online · Ao Vivo
          </h3>
        </div>
      )}

      <div className="flex flex-col flex-1 p-7 sm:p-9">
      {/* Benefícios · linhas editoriais */}
      <ul className="flex-1 border-t border-white/10 mb-8">
        {plan.benefits.map((b, j) => (
          <li
            key={j}
            className="flex items-center gap-4 py-3.5 border-b border-white/[0.07] text-sm text-brand-silver/90 leading-relaxed"
            data-testid={`plan-benefit-${plan.id}-${j}`}
          >
            <span className="h-1.5 w-1.5 rounded-full bg-brand-cyan shrink-0" />
            {b}
          </li>
        ))}
      </ul>

      {/* Seletor de quantidade */}
      <div className="flex items-center justify-between gap-4">
        <p className="font-sans text-sm font-semibold text-brand-silver/70">
          Escolha a quantidade de inscrições:
        </p>
        <div className="flex items-center gap-3">
          <button
            onClick={() => setQty((q) => Math.max(1, q - 1))}
            disabled={qty <= 1}
            className="h-10 w-10 rounded-full border border-white/15 bg-white/[0.05] flex items-center justify-center text-white hover:border-white/40 hover:bg-white/[0.12] disabled:opacity-30 disabled:pointer-events-none transition-all"
            aria-label="Diminuir quantidade"
            data-testid={`qty-minus-${plan.id}`}
          >
            <Minus size={15} />
          </button>
          <span className="font-display font-extrabold text-2xl text-white min-w-[2.25rem] text-center tabular-nums" data-testid={`qty-value-${plan.id}`}>
            {qty}
          </span>
          <button
            onClick={() => setQty((q) => Math.min(50, q + 1))}
            className="h-10 w-10 rounded-full bg-gradient-to-r from-brand-blue to-brand-cyan flex items-center justify-center text-brand-deep hover:scale-105 transition-all"
            aria-label="Aumentar quantidade"
            data-testid={`qty-plus-${plan.id}`}
          >
            <Plus size={15} />
          </button>
        </div>
      </div>

      {/* Aviso de desconto para grupos */}
      <div
        className={`mt-4 flex items-center gap-2.5 rounded-xl px-4 py-3 text-xs transition-all duration-300 ${
          hasDiscount
            ? "bg-white/[0.10] border border-white/25 text-white shadow-[inset_0_1px_0_rgba(255,255,255,0.12)]"
            : "bg-white/[0.04] border border-white/10 text-brand-silver/60"
        }`}
        data-testid={`discount-note-${plan.id}`}
      >
        <Users size={15} className="shrink-0" />
        <span className="whitespace-nowrap">
          {hasDiscount
            ? `Desconto de grupo aplicado: ${pct}% OFF`
            : `Descontos especiais para grupos a partir de ${GROUP_DISCOUNT_MIN} inscritos`}
        </span>
      </div>

      {/* Preço */}
      <div className="mt-6 pt-6 border-t border-white/10 flex items-end justify-between gap-3">
        <div>
          <p className="font-sans text-xs font-semibold text-brand-silver/60">
            {qty > 1 ? "por inscrição" : "investimento"}
          </p>
          {hasDiscount && (
            <p className="text-sm text-brand-silver/50 line-through" data-testid={`plan-unit-full-${plan.id}`}>
              {brl(plan.priceValue)}
            </p>
          )}
          <p className="font-display font-extrabold text-4xl sm:text-5xl text-gradient-blue leading-tight" data-testid={`plan-price-${plan.id}`}>
            {brl(unit)}
          </p>
        </div>
        {qty > 1 && (
          <div className="text-right">
            <p className="font-sans text-xs font-semibold text-brand-silver/60">total</p>
            {hasDiscount && (
              <p className="text-xs text-brand-silver/50 line-through" data-testid={`plan-total-full-${plan.id}`}>
                {brl(plan.priceValue * qty)}
              </p>
            )}
            <p className="font-display font-bold text-base sm:text-lg text-white leading-tight" data-testid={`plan-total-${plan.id}`}>
              {brl(total)}
            </p>
          </div>
        )}
      </div>

      {/* Tarja amarela · aviso de Lote 2 (presencial) */}
      {plan.id === "presencial" && (
        <div
          className="mt-5 rounded-lg bg-yellow-400 px-4 py-2.5 text-center"
          data-testid="plan-lot2-tag"
        >
          <span className="font-display font-bold text-[11px] sm:text-xs text-yellow-950 uppercase tracking-wide whitespace-nowrap">
            Lote 2 · Após 30/12/2026 o valor passa para R$ 5.797
          </span>
        </div>
      )}
      {plan.id === "online" && (
        <p className="mt-5 text-center text-xs text-brand-silver/50">{plan.priceNote}</p>
      )}

      <button
        onClick={() => onCTA(plan.id, qty)}
        className={`${plan.highlight ? "btn-primary" : "btn-ghost"} w-full mt-6`}
        data-testid={`plan-cta-${plan.id}`}
      >
        Garantir inscrição {plan.id === "presencial" ? "presencial" : "online"} <ArrowUpRight size={16} />
      </button>
      </div>
    </div>
  );
};

export default function Plans({ onCTA }) {
  return (
    <section id="ingressos" className="relative py-20 sm:py-28 overflow-hidden" data-testid="plans-section">
      <div className="absolute inset-0 bg-[radial-gradient(1000px_500px_at_50%_0%,rgba(0,142,255,0.10),transparent_60%)] pointer-events-none" />

      <div className="relative max-w-5xl mx-auto px-5 sm:px-8">
        <div className="text-center mb-12 sm:mb-16">
          <h2 className="heading-lg" data-testid="plans-heading">
            <MaskLine inView>Garanta a sua inscrição</MaskLine>
            <MaskLine inView delay={0.12}>
              <span className="text-gradient-blue">no PS27</span>
            </MaskLine>
          </h2>
          <Reveal delay={0.2}>
            <p className="mt-4 text-white/60 text-sm sm:text-base">
              {EVENT.dateLabel} · Florianópolis · SC. As vagas são limitadas e podem esgotar a qualquer momento.
            </p>
          </Reveal>
        </div>

        <div className="grid lg:grid-cols-2 gap-6 items-stretch">
          {PLANS.map((plan, i) => (
            <Reveal key={plan.id} delay={0.1 * i} className="h-full">
              <PlanCard plan={plan} onCTA={onCTA} />
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}
